import type { TrackPoint } from "./offline-draft";
import type { RouteGeoJson } from "./route-workflow";

export type RouteCoordinate = {
  latitude: number;
  longitude: number;
  elevation: number | null;
};

export type RouteWaypoint = RouteCoordinate & {
  id: string;
  name: string;
  kind: "start" | "finish" | "water" | "viewpoint" | "camp" | "shelter" | "danger" | "waypoint";
  description?: string;
};

export type ActivityMarker = RouteWaypoint & {
  timestamp: number;
};

export type GpsQuality = "precisa" | "regular" | "deficiente" | "sin señal";

export type NavigationStats = {
  routeDistanceKm: number;
  recordedDistanceKm: number;
  remainingDistanceKm: number;
  progress: number;
  elapsedSeconds: number;
  movingSeconds: number;
  estimatedRemainingSeconds: number | null;
  estimatedArrival: number | null;
  currentAltitudeM: number | null;
  minimumAltitudeM: number | null;
  maximumAltitudeM: number | null;
  ascentM: number;
  descentM: number;
  averageSpeedKmh: number;
  paceMinutesPerKm: number | null;
  offRouteDistanceM: number | null;
  offRoute: boolean;
  approachingStart: boolean;
  distanceToStartKm: number | null;
  bearingToStartDegrees: number | null;
  gpsQuality: GpsQuality;
  nextWaypoint: (RouteWaypoint & { distanceKm: number; estimatedSeconds: number | null }) | null;
};

export type ActivitySplit = {
  index: number;
  distanceKm: number;
  durationSeconds: number;
  paceSecondsPerKm: number;
  averageSpeedKmh: number;
  complete: boolean;
};

export type RouteSegmentPlan = {
  from: RouteWaypoint;
  to: RouteWaypoint;
  distanceKm: number;
  ascentM: number;
  descentM: number;
  minimumElevationM: number | null;
  maximumElevationM: number | null;
  directDistanceKm: number;
  intermediatePoints: number;
  legs: RouteSegmentLeg[];
  geojson: RouteGeoJson;
};

export type RouteSegmentLeg = {
  from: RouteWaypoint;
  to: RouteWaypoint;
  distanceKm: number;
  ascentM: number;
  descentM: number;
  minimumElevationM: number | null;
  maximumElevationM: number | null;
};

const EARTH_RADIUS_KM = 6371;
export const ROUTE_START_REACHED_M = 120;

export function haversineKm(
  a: Pick<RouteCoordinate, "latitude" | "longitude">,
  b: Pick<RouteCoordinate, "latitude" | "longitude">,
) {
  const radians = (value: number) => value * Math.PI / 180;
  const latitudeDelta = radians(b.latitude - a.latitude);
  const longitudeDelta = radians(b.longitude - a.longitude);
  const latitudeA = radians(a.latitude);
  const latitudeB = radians(b.latitude);
  const sinLatitude = Math.sin(latitudeDelta / 2);
  const sinLongitude = Math.sin(longitudeDelta / 2);
  const value = sinLatitude ** 2
    + Math.cos(latitudeA) * Math.cos(latitudeB) * sinLongitude ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

export function bearingDegrees(
  from: Pick<RouteCoordinate, "latitude" | "longitude">,
  to: Pick<RouteCoordinate, "latitude" | "longitude">,
) {
  const radians = (value: number) => value * Math.PI / 180;
  const fromLatitude = radians(from.latitude);
  const toLatitude = radians(to.latitude);
  const longitudeDelta = radians(to.longitude - from.longitude);
  const y = Math.sin(longitudeDelta) * Math.cos(toLatitude);
  const x = Math.cos(fromLatitude) * Math.sin(toLatitude)
    - Math.sin(fromLatitude) * Math.cos(toLatitude) * Math.cos(longitudeDelta);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

export function routeCoordinates(geojson?: RouteGeoJson): RouteCoordinate[] {
  const line = geojson?.features.find((feature) => feature.geometry.type === "LineString");
  if (!line || line.geometry.type !== "LineString") return [];
  return line.geometry.coordinates
    .map((coordinate) => ({
      longitude: Number(coordinate[0]),
      latitude: Number(coordinate[1]),
      elevation: Number.isFinite(coordinate[2]) ? Number(coordinate[2]) : null,
    }))
    .filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude));
}

export function routeWaypoints(geojson?: RouteGeoJson): RouteWaypoint[] {
  const points: RouteWaypoint[] = [];
  geojson?.features.forEach((feature, index) => {
    if (feature.geometry.type !== "Point") return;
    const coordinate = feature.geometry.coordinates;
    const longitude = Number(coordinate[0]);
    const latitude = Number(coordinate[1]);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
    const point: RouteWaypoint = {
      id: feature.properties.id || `waypoint-${index}`,
      name: feature.properties.name || `Punto ${index + 1}`,
      kind: waypointKind(feature.properties.kind),
      longitude,
      latitude,
      elevation: Number.isFinite(coordinate[2]) ? Number(coordinate[2]) : null,
    };
    if (feature.properties.description) point.description = feature.properties.description;
    points.push(point);
  });

  const line = routeCoordinates(geojson);
  if (line.length < 2) return points;
  const withEndpoints = [...points];
  if (!withEndpoints.some((point) => point.kind === "start")) {
    withEndpoints.unshift({ ...line[0], id: "route-start", name: "Inicio", kind: "start" });
  }
  if (!withEndpoints.some((point) => point.kind === "finish")) {
    withEndpoints.push({ ...line.at(-1)!, id: "route-finish", name: "Final", kind: "finish" });
  }
  return withEndpoints;
}

export function hasReachedRouteStart(
  geojson: RouteGeoJson | undefined,
  track: TrackPoint[],
  thresholdM = ROUTE_START_REACHED_M,
) {
  const start = routeCoordinates(geojson)[0];
  return Boolean(start && track.some((point) => haversineKm(point, start) * 1000 <= thresholdM));
}

function waypointKind(value?: string): RouteWaypoint["kind"] {
  return ["start", "finish", "water", "viewpoint", "camp", "shelter", "danger", "waypoint"].includes(value ?? "")
    ? value as RouteWaypoint["kind"]
    : "waypoint";
}

function cumulativeDistances(points: RouteCoordinate[]) {
  const distances = [0];
  for (let index = 1; index < points.length; index += 1) {
    distances.push(distances[index - 1] + haversineKm(points[index - 1], points[index]));
  }
  return distances;
}

function projectToSegment(
  point: Pick<RouteCoordinate, "latitude" | "longitude">,
  start: RouteCoordinate,
  finish: RouteCoordinate,
) {
  const latitude = point.latitude * Math.PI / 180;
  const metresPerLatitude = 111_320;
  const metresPerLongitude = Math.max(1, Math.cos(latitude) * 111_320);
  const bx = (finish.longitude - start.longitude) * metresPerLongitude;
  const by = (finish.latitude - start.latitude) * metresPerLatitude;
  const px = (point.longitude - start.longitude) * metresPerLongitude;
  const py = (point.latitude - start.latitude) * metresPerLatitude;
  const lengthSquared = bx * bx + by * by;
  const fraction = lengthSquared ? Math.max(0, Math.min(1, (px * bx + py * by) / lengthSquared)) : 0;
  const dx = px - bx * fraction;
  const dy = py - by * fraction;
  return { fraction, distanceM: Math.hypot(dx, dy) };
}

export function nearestRoutePosition(
  point: Pick<RouteCoordinate, "latitude" | "longitude">,
  route: RouteCoordinate[],
) {
  if (route.length < 2) return null;
  const cumulative = cumulativeDistances(route);
  let best = {
    distanceM: Number.POSITIVE_INFINITY,
    alongKm: 0,
    segment: 0,
    fraction: 0,
    coordinate: route[0],
  };
  for (let index = 1; index < route.length; index += 1) {
    const segmentStart = route[index - 1];
    const segmentFinish = route[index];
    const projection = projectToSegment(point, segmentStart, segmentFinish);
    if (projection.distanceM >= best.distanceM) continue;
    const segmentKm = cumulative[index] - cumulative[index - 1];
    best = {
      distanceM: projection.distanceM,
      alongKm: cumulative[index - 1] + segmentKm * projection.fraction,
      segment: index - 1,
      fraction: projection.fraction,
      coordinate: {
        longitude: segmentStart.longitude + (segmentFinish.longitude - segmentStart.longitude) * projection.fraction,
        latitude: segmentStart.latitude + (segmentFinish.latitude - segmentStart.latitude) * projection.fraction,
        elevation: segmentStart.elevation === null || segmentFinish.elevation === null
          ? null
          : segmentStart.elevation + (segmentFinish.elevation - segmentStart.elevation) * projection.fraction,
      },
    };
  }
  return best;
}

function segmentElevationMetrics(points: RouteCoordinate[]) {
  let ascentM = 0;
  let descentM = 0;
  const elevations = points
    .map((point) => point.elevation)
    .filter((value): value is number => value !== null && Number.isFinite(value));
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1].elevation;
    const current = points[index].elevation;
    if (previous === null || current === null) continue;
    const delta = current - previous;
    if (Math.abs(delta) < 1 || Math.abs(delta) >= 250) continue;
    if (delta > 0) ascentM += delta;
    else descentM += Math.abs(delta);
  }
  return {
    ascentM: Math.round(ascentM),
    descentM: Math.round(descentM),
    minimumElevationM: elevations.length ? Math.round(Math.min(...elevations)) : null,
    maximumElevationM: elevations.length ? Math.round(Math.max(...elevations)) : null,
  };
}

function sameCoordinate(a: RouteCoordinate, b: RouteCoordinate) {
  return Math.abs(a.latitude - b.latitude) < 1e-9
    && Math.abs(a.longitude - b.longitude) < 1e-9;
}

type LocatedWaypoint = {
  waypoint: RouteWaypoint;
  position: NonNullable<ReturnType<typeof nearestRoutePosition>>;
};

function routePointsBetween(
  route: RouteCoordinate[],
  from: LocatedWaypoint["position"],
  to: LocatedWaypoint["position"],
) {
  const forward = from.alongKm < to.alongKm;
  const first = forward ? from : to;
  const last = forward ? to : from;
  const points: RouteCoordinate[] = [first.coordinate];
  points.push(...route.slice(first.segment + 1, last.segment + 1));
  if (!sameCoordinate(points.at(-1)!, last.coordinate)) points.push(last.coordinate);
  return forward ? points : points.reverse();
}

/** Crea un GPX/GeoJSON navegable entre dos WPT sin alterar la ruta original. */
export function planRouteSegment(
  geojson: RouteGeoJson | undefined,
  fromId: string,
  toId: string,
): RouteSegmentPlan | null {
  const route = routeCoordinates(geojson);
  if (route.length < 2 || fromId === toId) return null;
  const entries = routeWaypoints(geojson)
    .map((waypoint) => ({ waypoint, position: nearestRoutePosition(waypoint, route) }))
    .filter((entry): entry is LocatedWaypoint => Boolean(entry.position));
  const fromEntry = entries.find((entry) => entry.waypoint.id === fromId);
  const toEntry = entries.find((entry) => entry.waypoint.id === toId);
  if (!fromEntry || !toEntry || Math.abs(fromEntry.position.alongKm - toEntry.position.alongKm) < 0.001) return null;

  const forward = fromEntry.position.alongKm < toEntry.position.alongKm;
  const lowerKm = Math.min(fromEntry.position.alongKm, toEntry.position.alongKm);
  const upperKm = Math.max(fromEntry.position.alongKm, toEntry.position.alongKm);
  const intermediateEntries = entries
    .filter((entry) => entry.waypoint.id !== fromEntry.waypoint.id
      && entry.waypoint.id !== toEntry.waypoint.id
      && entry.position.alongKm > lowerKm + 0.001
      && entry.position.alongKm < upperKm - 0.001)
    .sort((a, b) => a.position.alongKm - b.position.alongKm);
  if (!forward) intermediateEntries.reverse();
  const selectedEntries = [fromEntry, ...intermediateEntries, toEntry];
  const points = routePointsBetween(route, fromEntry.position, toEntry.position);
  const metrics = segmentElevationMetrics(points);
  const selectedWaypoints = selectedEntries.map((entry, index) => ({
    ...entry.waypoint,
    kind: index === 0 ? "start" as const : index === selectedEntries.length - 1 ? "finish" as const : entry.waypoint.kind,
  }));
  const legs = selectedEntries.slice(1).map((entry, index) => {
    const previous = selectedEntries[index];
    const legMetrics = segmentElevationMetrics(routePointsBetween(route, previous.position, entry.position));
    return {
      from: previous.waypoint,
      to: entry.waypoint,
      distanceKm: Math.abs(entry.position.alongKm - previous.position.alongKm),
      ...legMetrics,
    };
  });
  const segmentGeojson: RouteGeoJson = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: { source: "THERA", segment: "true", from: fromEntry.waypoint.name, to: toEntry.waypoint.name },
        geometry: {
          type: "LineString",
          coordinates: points.map((point) => point.elevation === null
            ? [point.longitude, point.latitude]
            : [point.longitude, point.latitude, point.elevation]),
        },
      },
      ...selectedWaypoints.map((waypoint) => ({
        type: "Feature" as const,
        properties: {
          id: waypoint.id,
          name: waypoint.name,
          kind: waypoint.kind,
          description: waypoint.description ?? "",
        },
        geometry: {
          type: "Point" as const,
          coordinates: waypoint.elevation === null
            ? [waypoint.longitude, waypoint.latitude]
            : [waypoint.longitude, waypoint.latitude, waypoint.elevation],
        },
      })),
    ],
  };
  return {
    from: fromEntry.waypoint,
    to: toEntry.waypoint,
    distanceKm: Math.abs(toEntry.position.alongKm - fromEntry.position.alongKm),
    directDistanceKm: haversineKm(fromEntry.waypoint, toEntry.waypoint),
    intermediatePoints: Math.max(0, selectedWaypoints.length - 2),
    legs,
    ...metrics,
    geojson: segmentGeojson,
  };
}

export function acceptedTrackPoint(previous: TrackPoint | undefined, point: TrackPoint) {
  if (!Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return false;
  if (!Number.isFinite(point.accuracy) || point.accuracy > 100) return false;
  if (!previous) return true;
  const elapsedSeconds = (point.timestamp - previous.timestamp) / 1000;
  if (elapsedSeconds <= 0) return false;
  const distanceKm = haversineKm(previous, point);
  const speedKmh = distanceKm / (elapsedSeconds / 3600);
  if (speedKmh > 45) return false;
  const jitterFloorM = Math.min(8, Math.max(2, Math.min(previous.accuracy, point.accuracy) * 0.08));
  return distanceKm * 1000 >= jitterFloorM || elapsedSeconds >= 20;
}

export function gpsQuality(accuracy?: number | null): GpsQuality {
  if (accuracy === null || accuracy === undefined || !Number.isFinite(accuracy)) return "sin señal";
  if (accuracy <= 15) return "precisa";
  if (accuracy <= 40) return "regular";
  return "deficiente";
}

function activeSegmentSeconds(previous: TrackPoint, point: TrackPoint) {
  if (Number.isFinite(previous.activeSeconds) && Number.isFinite(point.activeSeconds)) {
    return Math.max(0, point.activeSeconds! - previous.activeSeconds!);
  }
  const gpsSeconds = Math.max(0, (point.timestamp - previous.timestamp) / 1000);
  // Las actividades antiguas no guardaban el reloj activo. Un intervalo GPS
  // largo suele corresponder a una pausa o a una recuperación de la sesión.
  return Math.min(gpsSeconds, 120);
}

/**
 * Calcula parciales de 1 km e interpola el instante exacto en que se cruzó
 * cada límite. El último tramo incompleto se incluye desde 100 m.
 */
export function activitySplits(track: TrackPoint[], minimumPartialKm = 0.1): ActivitySplit[] {
  if (track.length < 2) return [];
  const splits: ActivitySplit[] = [];
  let cumulativeDistanceKm = 0;
  let cumulativeSeconds = 0;
  let previousBoundarySeconds = 0;
  let previousBoundaryDistanceKm = 0;
  let nextBoundaryKm = 1;

  for (let index = 1; index < track.length; index += 1) {
    const previous = track[index - 1];
    const point = track[index];
    const segmentKm = haversineKm(previous, point);
    if (segmentKm <= 0) continue;
    const segmentSeconds = activeSegmentSeconds(previous, point);
    const distanceBeforeSegment = cumulativeDistanceKm;
    const secondsBeforeSegment = cumulativeSeconds;
    cumulativeDistanceKm += segmentKm;
    cumulativeSeconds += segmentSeconds;

    while (nextBoundaryKm <= cumulativeDistanceKm + 1e-9) {
      const fraction = Math.max(0, Math.min(1, (nextBoundaryKm - distanceBeforeSegment) / segmentKm));
      const boundarySeconds = secondsBeforeSegment + segmentSeconds * fraction;
      const splitDistanceKm = nextBoundaryKm - previousBoundaryDistanceKm;
      const durationSeconds = Math.max(0, boundarySeconds - previousBoundarySeconds);
      splits.push({
        index: splits.length + 1,
        distanceKm: splitDistanceKm,
        durationSeconds,
        paceSecondsPerKm: splitDistanceKm > 0 ? durationSeconds / splitDistanceKm : 0,
        averageSpeedKmh: durationSeconds > 0 ? splitDistanceKm / (durationSeconds / 3600) : 0,
        complete: true,
      });
      previousBoundarySeconds = boundarySeconds;
      previousBoundaryDistanceKm = nextBoundaryKm;
      nextBoundaryKm += 1;
    }
  }

  const remainingKm = cumulativeDistanceKm - previousBoundaryDistanceKm;
  if (remainingKm + 1e-9 >= minimumPartialKm) {
    const durationSeconds = Math.max(0, cumulativeSeconds - previousBoundarySeconds);
    splits.push({
      index: splits.length + 1,
      distanceKm: remainingKm,
      durationSeconds,
      paceSecondsPerKm: durationSeconds / remainingKm,
      averageSpeedKmh: durationSeconds > 0 ? remainingKm / (durationSeconds / 3600) : 0,
      complete: false,
    });
  }
  return splits;
}

function trackMetrics(track: TrackPoint[]) {
  let distanceKm = 0;
  let movingSeconds = 0;
  let ascentM = 0;
  let descentM = 0;
  const altitudes = track.map((point) => point.altitude).filter((value): value is number => value !== null && Number.isFinite(value));
  for (let index = 1; index < track.length; index += 1) {
    const segmentKm = haversineKm(track[index - 1], track[index]);
    const elapsed = Math.max(0, (track[index].timestamp - track[index - 1].timestamp) / 1000);
    distanceKm += segmentKm;
    if (segmentKm * 1000 >= 2 && elapsed <= 120) movingSeconds += elapsed;
    const previousAltitude = track[index - 1].altitude;
    const altitude = track[index].altitude;
    if (previousAltitude !== null && altitude !== null) {
      const delta = altitude - previousAltitude;
      if (Math.abs(delta) >= 1.5 && Math.abs(delta) < 80) {
        if (delta > 0) ascentM += delta;
        else descentM += Math.abs(delta);
      }
    }
  }
  return {
    distanceKm,
    movingSeconds: Math.round(movingSeconds),
    ascentM: Math.round(ascentM),
    descentM: Math.round(descentM),
    minimumAltitudeM: altitudes.length ? Math.round(Math.min(...altitudes)) : null,
    maximumAltitudeM: altitudes.length ? Math.round(Math.max(...altitudes)) : null,
  };
}

export function navigationStats(
  geojson: RouteGeoJson | undefined,
  track: TrackPoint[],
  elapsedSeconds: number,
  routeStarted = true,
): NavigationStats {
  const route = routeCoordinates(geojson);
  const cumulative = cumulativeDistances(route);
  const routeDistanceKm = cumulative.at(-1) ?? 0;
  const metrics = trackMetrics(track);
  const current = track.at(-1);
  const nearest = current ? nearestRoutePosition(current, route) : null;
  const routeStart = route[0];
  const distanceToStartKm = current && routeStart ? haversineKm(current, routeStart) : null;
  const bearingToStartDegrees = current && routeStart ? bearingDegrees(current, routeStart) : null;
  const approachingStart = Boolean(!routeStarted && current && routeStart);
  const alongKm = routeStarted ? nearest?.alongKm ?? 0 : 0;
  const remainingDistanceKm = Math.max(0, routeDistanceKm - alongKm);
  const progress = routeDistanceKm ? Math.min(100, alongKm / routeDistanceKm * 100) : 0;
  const averageSpeedKmh = metrics.movingSeconds > 0 ? metrics.distanceKm / (metrics.movingSeconds / 3600) : 0;
  const estimatedRemainingSeconds = routeStarted && averageSpeedKmh >= 0.5
    ? Math.round(remainingDistanceKm / averageSpeedKmh * 3600)
    : null;

  const routeWaypointList = routeWaypoints(geojson);
  const waypoints = routeWaypointList
    .map((waypoint) => ({ waypoint, position: nearestRoutePosition(waypoint, route) }))
    .filter((entry) => entry.position && entry.position.alongKm > alongKm + 0.01)
    .sort((a, b) => a.position!.alongKm - b.position!.alongKm);
  const next = waypoints[0];
  const nextDistanceKm = next ? Math.max(0, next.position!.alongKm - alongKm) : 0;
  const offRouteLimitM = Math.max(50, (current?.accuracy ?? 0) * 1.5);
  const startWaypoint = routeWaypointList.find((waypoint) => waypoint.kind === "start")
    ?? (routeStart ? { ...routeStart, id: "route-start", name: "Inicio de la ruta", kind: "start" as const } : null);
  const nextWaypoint = approachingStart && startWaypoint && distanceToStartKm !== null
    ? {
      ...startWaypoint,
      name: "Inicio de la ruta",
      distanceKm: distanceToStartKm,
      estimatedSeconds: null,
    }
    : next ? {
      ...next.waypoint,
      distanceKm: nextDistanceKm,
      estimatedSeconds: averageSpeedKmh >= 0.5 ? Math.round(nextDistanceKm / averageSpeedKmh * 3600) : null,
    } : null;

  return {
    routeDistanceKm,
    recordedDistanceKm: metrics.distanceKm,
    remainingDistanceKm,
    progress,
    elapsedSeconds,
    movingSeconds: metrics.movingSeconds,
    estimatedRemainingSeconds,
    estimatedArrival: estimatedRemainingSeconds === null ? null : Date.now() + estimatedRemainingSeconds * 1000,
    currentAltitudeM: current?.altitude === null || current?.altitude === undefined ? null : Math.round(current.altitude),
    minimumAltitudeM: metrics.minimumAltitudeM,
    maximumAltitudeM: metrics.maximumAltitudeM,
    ascentM: metrics.ascentM,
    descentM: metrics.descentM,
    averageSpeedKmh,
    paceMinutesPerKm: averageSpeedKmh >= 0.5 ? 60 / averageSpeedKmh : null,
    offRouteDistanceM: nearest ? Math.round(nearest.distanceM) : null,
    offRoute: Boolean(routeStarted && nearest && nearest.distanceM > offRouteLimitM),
    approachingStart,
    distanceToStartKm,
    bearingToStartDegrees,
    gpsQuality: gpsQuality(current?.accuracy),
    nextWaypoint,
  };
}

export function trimTrackEndpoints(track: TrackPoint[], metres = 200) {
  if (track.length < 3 || metres <= 0) return track;
  let fromStart = 0;
  let startIndex = 0;
  while (startIndex < track.length - 1 && fromStart < metres / 1000) {
    fromStart += haversineKm(track[startIndex], track[startIndex + 1]);
    startIndex += 1;
  }
  let fromEnd = 0;
  let endIndex = track.length - 1;
  while (endIndex > startIndex && fromEnd < metres / 1000) {
    fromEnd += haversineKm(track[endIndex], track[endIndex - 1]);
    endIndex -= 1;
  }
  return endIndex - startIndex >= 1 ? track.slice(startIndex, endIndex + 1) : track;
}

export function activityGeoJson(track: TrackPoint[], markers: ActivityMarker[] = []) {
  return {
    type: "FeatureCollection" as const,
    features: [
      {
        type: "Feature" as const,
        properties: { source: "THERA" },
        geometry: {
          type: "LineString" as const,
          coordinates: track.map((point) => point.altitude === null
            ? [point.longitude, point.latitude]
            : [point.longitude, point.latitude, point.altitude]),
        },
      },
      ...markers.map((marker) => ({
        type: "Feature" as const,
        properties: { id: marker.id, name: marker.name, kind: marker.kind, description: marker.description ?? "", timestamp: String(marker.timestamp) },
        geometry: {
          type: "Point" as const,
          coordinates: marker.elevation === null
            ? [marker.longitude, marker.latitude]
            : [marker.longitude, marker.latitude, marker.elevation],
        },
      })),
    ],
  };
}
